import * as Sentry from '@sentry/react'
import {
  ApolloClient,
  InMemoryCache,
  ServerError,
  createHttpLink,
  from,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { getAPIUrl } from '@utils/url'
import { isIgnorableConnectionProblem } from '@utils/ignorableNetworkError'
import { markHandledSessionError } from '@utils/handledSessionError'
import { cleanAndLogout } from '@utils/session'
import { safeStringify } from '@utils/safeStringify'

type GraphQLResponseError = {
  message?: string
  extensions?: {
    code?: string
    reason?: string
  }
}

const isAuthenticationError = (error: GraphQLResponseError) => {
  const code = error.extensions?.code

  return (
    error.message?.includes('Authentication required') ||
    code === 'UNAUTHENTICATED'
  )
}

let clientSelectionRedirectStarted = false

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (networkError) {
    const serverError = networkError as ServerError & {
      response?: Response
      result?: {
        data?: unknown
        errors?: GraphQLResponseError[]
      }
    }
    const statusCode =
      'statusCode' in networkError &&
      typeof networkError.statusCode === 'number'
        ? networkError.statusCode
        : undefined

    // Authentication failures raised while creating the GraphQL context are
    // currently wrapped by the API in an HTTP 500 response.
    const responseErrors = serverError.result?.errors ?? []
    const isClientSelectionError = (error: GraphQLResponseError) =>
      statusCode === 403 && error.extensions?.reason === 'CLIENT_NOT_SELECTED'

    if (statusCode === 401 || responseErrors.some(isAuthenticationError)) {
      const onlySessionErrors = responseErrors.every(isAuthenticationError)
      if (onlySessionErrors) {
        markHandledSessionError(networkError)
      }
      cleanAndLogout()
      if (onlySessionErrors) return
    } else if (responseErrors.some(isClientSelectionError)) {
      const onlyClientSelectionErrors = responseErrors.every(
        isClientSelectionError,
      )
      if (onlyClientSelectionErrors) markHandledSessionError(networkError)

      if (
        !clientSelectionRedirectStarted &&
        window.location.pathname !== '/clients'
      ) {
        clientSelectionRedirectStarted = true
        window.location.replace('/clients')
      }
      if (onlyClientSelectionErrors) return
    }

    if (isIgnorableConnectionProblem(networkError)) {
      return
    }

    const errorCategory =
      statusCode === undefined
        ? 'unknown_error'
        : statusCode >= 500
        ? 'server_error'
        : statusCode >= 400
        ? 'client_error'
        : 'unknown_error'

    Sentry.captureException(networkError, {
      tags: {
        type: 'apollo-network-error',
        error_category: errorCategory,
        operationName: operation.operationName,
        statusCode: statusCode ? String(statusCode) : 'unknown',
      },
      extra: {
        variables: safeStringify(operation.variables, 2),
        statusCode,
        responseUrl: serverError?.response?.url,
        result: safeStringify(serverError?.result, 2),
      },
      fingerprint: [
        'apollo-network-error',
        String(statusCode ?? 'unknown'),
        operation.operationName,
      ],
    })
  }

  if (!graphQLErrors) {
    return
  }

  for (const err of graphQLErrors) {
    if (isAuthenticationError(err)) {
      markHandledSessionError(err)
      cleanAndLogout()
    }
  }
})

const httpLink = createHttpLink({
  uri: getAPIUrl('/graphql'),
  credentials: 'include',
})

const link = from([errorLink, httpLink])

const cache = new InMemoryCache({
  possibleTypes: {
    Mention: [
      'TwitterMention',
      'FacebookMention',
      'InstagramMention',
      'YoutubeMention',
      'MediaMention',
      'ReviewMention',
      'TiktokMention',
      'LinkedinMention',
    ],
    MentionGroup: [
      'FeedGroup',
      'CategoryGroup',
      'AspectGroup',
      'ThemeGroup',
      'SentimentGroup',
      'SourceGroup',
      'TimeGroup',
      'TypeGroup',
    ],
  },
  typePolicies: {
    LinkedinAuthor: {
      keyFields: ['urn'],
    },
  },
})

const client = new ApolloClient({
  link,
  cache,
})

export default client
